import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

export default function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const [nowa, setNowa] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showRoleOverlay, setShowRoleOverlay] = useState(false)
  const [overlayRole, setOverlayRole] = useState('')

  const getRoleIcon = () => {
    const currentRole = useAuthStore.getState().role
    return (
      <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {currentRole === 'super_admin' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        )}
      </svg>
    )
  }

  const getRoleLabel = () => {
    const currentRole = useAuthStore.getState().role
    switch (currentRole) {
      case 'super_admin':
        return 'Super Admin'
      case 'stadion':
        return 'Admin Wilayah'
      default:
        return 'Admin'
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nowa.trim()) {
      setError('No. WhatsApp wajib diisi')
      return
    }
    if (!nowa.startsWith('08')) {
      setError('Format harus 081xxxxxxxx')
      return
    }
    if (nowa.length < 10 || nowa.length > 14) {
      setError('No. WhatsApp minimal 10 digit')
      return
    }
    if (!password.trim()) {
      setError('Password wajib diisi')
      return
    }

    setError('')
    setLoading(true)

    try {
      await login(nowa.trim(), password)
      setOverlayRole(getRoleLabel())
      setShowRoleOverlay(true)
      setLoading(false)
      setTimeout(() => {
        setShowRoleOverlay(false)
        navigate('/dashboard', { replace: true })
      }, 700)
    } catch (err: unknown) {
      const e = err as { response?: { message?: string }; message?: string }
      const msg = e?.response?.message || e?.message || ''
      if (msg.toLowerCase().includes('failed') || msg.toLowerCase().includes('auth')) {
        setError('No WA atau password salah.')
      } else {
        setError('Login gagal. Periksa No WA dan password Anda.')
      }
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap');
      `}</style>

      {/* Main login container */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 relative">
        {/* Back button - open in same tab if from drawer, otherwise new tab */}
        <div className="absolute top-4 left-4">
          <a
            href="https://iswarajatim.com"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            title="Kembali ke ISWARA"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </a>
        </div>

        {/* Logo */}
        <div className="mb-6">
          <img
            src="/assets/images/Iswara_logo.png"
            alt="ISWARA"
            className="h-24 w-auto object-contain"
          />
        </div>

        {/* Title */}
        <h1
          className="text-xl font-bold tracking-wider mb-6"
          style={{ fontFamily: 'Rajdhani, sans-serif', color: '#1B5E20' }}
        >
          KEGIATAN ISWARA JATIM
        </h1>

        {/* Wrapper with form container + copyright */}
        <div className="w-full max-w-sm relative">
          {/* Container */}
          <div className="w-full bg-white rounded-2xl shadow-lg px-6 py-6">
            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Error */}
              {error && (
                <div className="p-3 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200">
                  {error}
                </div>
              )}

              {/* No. WhatsApp */}
              <div>
                <label
                  className="block text-sm font-semibold mb-1"
                  style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(27,94,32,0.7)' }}
                >
                  No. WhatsApp
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#22C55E">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <input
                    type="tel"
                    value={nowa}
                    onChange={(e) => setNowa(e.target.value)}
                    placeholder="08123456789"
                    inputMode="tel"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm font-medium outline-none transition-all"
                    style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      borderColor: '#E5E7EB',
                      backgroundColor: '#fff',
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  className="block text-sm font-semibold mb-1"
                  style={{ fontFamily: 'Rajdhani, sans-serif', color: 'rgba(27,94,32,0.7)' }}
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#EAB308">
                      <path d="M12 1C8.676 1 6 3.676 6 7v2H4v14h16V9h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
                    </svg>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Masukkan password"
                    className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm font-medium outline-none transition-all"
                    style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      borderColor: '#E5E7EB',
                      backgroundColor: '#fff',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Masuk Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl text-white font-bold text-base tracking-wider transition-all shadow-sm hover:shadow-md disabled:opacity-60 mt-2"
                style={{ fontFamily: 'Rajdhani, sans-serif', backgroundColor: '#1B5E20' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    MEMPROSES...
                  </span>
                ) : (
                  'Masuk'
                )}
              </button>
            </form>
          </div>

          {/* Copyright - below container, right aligned */}
          <div className="text-right mt-2 pr-1">
            <p className="text-[10px] italic" style={{ color: '#9CA3AF', fontFamily: 'Rajdhani, sans-serif' }}>
              @2026 developed by ephiewae
            </p>
          </div>
        </div>
      </div>

      {/* Role Overlay - 700ms after successful login */}
      {showRoleOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55">
          <div
            className="mx-10 px-9 py-7 rounded-2xl text-center"
            style={{
              background: 'linear-gradient(135deg, #1B5E20 0%, rgba(27,94,32,0.85) 100%)',
              boxShadow: '0 10px 24px rgba(27,94,32,0.5)',
            }}
          >
            <div className="w-12 h-12 rounded-full bg-white/18 flex items-center justify-center mx-auto mb-4">
              {getRoleIcon()}
            </div>
            <p
              className="text-white/90 text-sm tracking-wider mb-1"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Anda login sebagai
            </p>
            <p
              className="text-white text-2xl font-bold tracking-wide"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              {overlayRole}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
